/**
 * Row model of Bulk Creation step 02 (R17, R20–R23, R58).
 *
 * The table is an explicit list of rows, not a zip of two sorted file lists:
 * a drop only sorts its own files and fills the rows that lack that side,
 * deleting never re-pairs, and the arrows swap backs between neighbours. All
 * operations are pure (rows in, rows out) so the store stays a thin wrapper.
 */

import { deriveTitle } from './derive-title';
import { naturalSort } from './natural-sort';

/** Upload zone, and the side of a row a cell sits on. */
export type BulkZone = 'front' | 'back';

export type BulkCellState = 'uploading' | 'ready' | 'error';

/** One uploaded (or uploading) motif file. */
export interface BulkCell {
  id: string;
  fileName: string;
  fileSize: number;
  state: BulkCellState;
  /** Set once the upload finished; dimensions come from the server response. */
  assetId?: string;
  url?: string;
  width?: number;
  height?: number;
  /** Error code of a failed upload, translated by the UI. */
  errorCode?: string;
}

/** One future design: a front and/or a back motif plus its working title. */
export interface BulkRow {
  id: string;
  front?: BulkCell;
  back?: BulkCell;
  title: string;
  /** The seller typed a title; automatic derivation leaves it alone. */
  titleEdited: boolean;
}

/** The minimum a dropped file must expose; `File` satisfies it. */
export interface BulkFileInput {
  name: string;
  size: number;
}

/** Server data of a finished upload. */
export interface BulkUploadedAsset {
  assetId: string;
  url: string;
  width: number;
  height: number;
}

/** Product facts the row counts need. */
export interface BulkCountProduct {
  hasBack: boolean;
}

export type MoveDirection = 'up' | 'down';

let idSequence = 0;

/** Ids only need to be unique within the page session (state is not persisted). */
function nextId(prefix: 'row' | 'cell'): string {
  idSequence += 1;
  return `bulk-${prefix}-${idSequence}`;
}

/** Title a row gets when the seller has not edited it: front name, else back. */
function autoTitle(row: Pick<BulkRow, 'front' | 'back'>): string {
  const source = row.front ?? row.back;
  return source ? deriveTitle(source.fileName) : '';
}

/** Re-derive the title of an unedited row after its cells changed. */
function withDerivedTitle(row: BulkRow): BulkRow {
  if (row.titleEdited) return row;
  const title = autoTitle(row);
  return title === row.title ? row : { ...row, title };
}

function isEmptyRow(row: BulkRow): boolean {
  return row.front === undefined && row.back === undefined;
}

function isDuplicate(
  existing: readonly BulkCell[],
  file: BulkFileInput
): boolean {
  const name = file.name.normalize('NFC');
  return existing.some(
    (cell) =>
      cell.fileSize === file.size && cell.fileName.normalize('NFC') === name
  );
}

export interface AddFilesResult<F extends BulkFileInput> {
  rows: BulkRow[];
  /** Accepted files in table order, with the cell each one now occupies. */
  added: Array<{ cellId: string; rowId: string; file: F }>;
  /** Files rejected because the zone already holds the same name and size. */
  duplicates: F[];
}

/**
 * Drop files into a zone: sort only the new files naturally, fill the rows
 * lacking that side in row order, then append rows. A file with the same name
 * and size as a cell already in the zone (or earlier in the same drop) is
 * rejected; the same name in the other zone is fine.
 */
export function addFilesToRows<F extends BulkFileInput>(
  rows: readonly BulkRow[],
  zone: BulkZone,
  files: readonly F[]
): AddFilesResult<F> {
  const next = [...rows];
  const zoneCells = rows.flatMap((row) => (row[zone] ? [row[zone]] : []));
  const added: AddFilesResult<F>['added'] = [];
  const duplicates: F[] = [];

  for (const file of naturalSort(files, (f) => f.name)) {
    if (isDuplicate(zoneCells, file)) {
      duplicates.push(file);
      continue;
    }

    const cell: BulkCell = {
      id: nextId('cell'),
      fileName: file.name,
      fileSize: file.size,
      state: 'uploading',
    };
    zoneCells.push(cell);

    const index = next.findIndex((row) => row[zone] === undefined);
    const base: BulkRow = (index === -1 ? undefined : next[index]) ?? {
      id: nextId('row'),
      title: '',
      titleEdited: false,
    };
    const filled = withDerivedTitle({ ...base, [zone]: cell });
    if (index === -1) next.push(filled);
    else next[index] = filled;
    added.push({ cellId: cell.id, rowId: filled.id, file });
  }

  return { rows: next, added, duplicates };
}

/** Empty one side of a row; a row with neither side left disappears. */
export function removeCellFromRows(
  rows: readonly BulkRow[],
  rowId: string,
  zone: BulkZone
): BulkRow[] {
  return rows.flatMap((row) => {
    if (row.id !== rowId) return [row];
    const emptied: BulkRow = { ...row, [zone]: undefined };
    return isEmptyRow(emptied) ? [] : [withDerivedTitle(emptied)];
  });
}

/** Delete a row with both cells; the other rows keep their pairing. */
export function removeRowFromRows(
  rows: readonly BulkRow[],
  rowId: string
): BulkRow[] {
  return rows.filter((row) => row.id !== rowId);
}

/**
 * Move a row's back to the neighbouring row, swapping with whatever back sits
 * there. Up on the first row and moving an absent back are no-ops; down on
 * the last row moves the back into a new back-only row. Rows left without any
 * cell are dropped.
 */
export function moveBackInRows(
  rows: readonly BulkRow[],
  rowId: string,
  direction: MoveDirection
): BulkRow[] {
  const index = rows.findIndex((row) => row.id === rowId);
  const row = rows[index];
  if (!row?.back) return [...rows];

  const next = [...rows];
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0) return next;

  if (targetIndex >= rows.length) {
    // Moving the back out of a back-only last row would recreate the same row.
    if (!row.front) return next;
    next[index] = withDerivedTitle({ ...row, back: undefined });
    next.push(
      withDerivedTitle({
        id: nextId('row'),
        back: row.back,
        title: '',
        titleEdited: false,
      })
    );
    return next;
  }

  const target = next[targetIndex];
  if (!target) return next;
  next[index] = withDerivedTitle({ ...row, back: target.back });
  next[targetIndex] = withDerivedTitle({ ...target, back: row.back });
  return next.filter((r) => !isEmptyRow(r));
}

/** Set a seller-typed title; from now on derivation leaves the row alone. */
export function setRowTitle(
  rows: readonly BulkRow[],
  rowId: string,
  title: string
): BulkRow[] {
  return rows.map((row) =>
    row.id === rowId ? { ...row, title, titleEdited: true } : row
  );
}

/** Apply a change to the cell with `cellId`, wherever it sits. */
export function updateCellInRows(
  rows: readonly BulkRow[],
  cellId: string,
  update: (cell: BulkCell) => BulkCell
): BulkRow[] {
  return rows.map((row) => {
    if (row.front?.id === cellId) return { ...row, front: update(row.front) };
    if (row.back?.id === cellId) return { ...row, back: update(row.back) };
    return row;
  });
}

// ─── Derived counts ──────────────────────────────────────────────────

/** Backs are only used when at least one active product carries a back (R15, R48). */
export function isBackEnabled(
  activeProducts: readonly BulkCountProduct[]
): boolean {
  return activeProducts.some((product) => product.hasBack);
}

/**
 * Whether a cell contributes to a design. Error cells never do; a back is
 * "ignored" when no active product has a back. Uploading cells count so the
 * table does not jump while uploads run (the CTA is blocked separately).
 */
export function isCountingCell(
  cell: BulkCell | undefined,
  zone: BulkZone,
  backEnabled: boolean
): cell is BulkCell {
  if (!cell || cell.state === 'error') return false;
  return zone === 'front' || backEnabled;
}

/**
 * Products a row will be created on (R23): all active products when the
 * front counts, only active products with a back for a back-only row.
 */
export function rowProductCount(
  row: BulkRow,
  activeProducts: readonly BulkCountProduct[]
): number {
  const backEnabled = isBackEnabled(activeProducts);
  if (isCountingCell(row.front, 'front', backEnabled)) {
    return activeProducts.length;
  }
  if (isCountingCell(row.back, 'back', backEnabled)) {
    return activeProducts.filter((product) => product.hasBack).length;
  }
  return 0;
}

export interface BulkStatusCounts {
  /** Front cells that count (not in error). */
  frontCount: number;
  /** Back cells not in error, including ignored ones (see `backIgnored`). */
  backCount: number;
  /** Rows with at least one counting cell. */
  designCount: number;
  /** Designs with exactly one counting side; always 0 while backs are ignored. */
  incompletePairs: number;
  /** Sum of the row product counts (footer, CTA). */
  totalProducts: number;
  /** Backs were uploaded but no active product has a back. */
  backIgnored: boolean;
}

export function computeStatusCounts(
  rows: readonly BulkRow[],
  activeProducts: readonly BulkCountProduct[]
): BulkStatusCounts {
  const backEnabled = isBackEnabled(activeProducts);
  let frontCount = 0;
  let backCount = 0;
  let designCount = 0;
  let incompletePairs = 0;
  let totalProducts = 0;

  for (const row of rows) {
    const front = isCountingCell(row.front, 'front', backEnabled);
    const back = isCountingCell(row.back, 'back', backEnabled);
    if (front) frontCount += 1;
    if (row.back && row.back.state !== 'error') backCount += 1;
    if (front || back) designCount += 1;
    if (backEnabled && front !== back) incompletePairs += 1;
    totalProducts += rowProductCount(row, activeProducts);
  }

  return {
    frontCount,
    backCount,
    designCount,
    incompletePairs,
    totalProducts,
    backIgnored: !backEnabled && backCount > 0,
  };
}

/** Sum of the product counts of all rows (R24). */
export function totalProductCount(
  rows: readonly BulkRow[],
  activeProducts: readonly BulkCountProduct[]
): number {
  return rows.reduce(
    (sum, row) => sum + rowProductCount(row, activeProducts),
    0
  );
}

/**
 * A row that will be sent (it has a counting cell) but whose working title
 * is empty or only spaces. The server rejects such a run, and the title is
 * never re-derived once the seller edited it, so the CTA blocks instead.
 */
export function isRowTitleMissing(row: BulkRow, backEnabled: boolean): boolean {
  const counts =
    isCountingCell(row.front, 'front', backEnabled) ||
    isCountingCell(row.back, 'back', backEnabled);
  return counts && row.title.trim() === '';
}

/** Any cell still uploading or failed; both block the CTA (R58). */
export function hasPendingOrFailedCells(rows: readonly BulkRow[]): boolean {
  return rows.some((row) =>
    [row.front, row.back].some(
      (cell) => cell?.state === 'uploading' || cell?.state === 'error'
    )
  );
}
